import React, {useState} from "react";
import { TextField, Button, Typography, CircularProgress } from "@material-ui/core";
import {CardNumberElement, CardExpiryElement, CardCvcElement, useElements, useStripe} from "@stripe/react-stripe-js"
import StripeInput from "./stripe-input";
import Info from "@material-ui/icons/Info";
import {AccountService} from "../../services";
import {useStore} from "../../store";
import {useSnackbar} from "notistack";
import * as toast from "../../utils/toast";
import {setAccountPaymentMethod} from "../../store/actions";

const AddPaymentMethod = ({accountId=0}) => {
    const elements = useElements();
    const stripe = useStripe();
    const [state, setState] = useState({
        cardholder: {error: '', complete: false, value: ''},
        cardNumber: {error: '', complete: false},
        cardExpiry: {error: '', complete: false},
        cardCvc:    {error: '', complete: false},
        done: false,
        isFetching: false,
    })
    const [, dispatch] = useStore();
    const {enqueueSnackbar, closeSnackbar} = useSnackbar();     // Snackbar Hook for notifications

    // Styling for stripe elements
    const elOptions = {
        style: {
            base: {
                fontFamily: 'Mulish',
                fontSize: '16px',
            }
        }
    }

    // If stripe.js hasn't loaded yet, we'll get a null value
    if (!elements || !stripe) {
        return null
    }

    const canSubmit = () => {
        return state.cardholder.complete &&
            state.cardNumber.complete &&
            state.cardExpiry.complete &&
            state.cardCvc.complete
    }

    const onCardholderChange = (e) => {
        updateCardholder(e.target.value);
    }

    const validateCardholder = () => {
        updateCardholder(state.cardholder.value);
    }

    const updateCardholder = (value) => {
        const cardholder = {
            error: value.length === 0 ? 'Cardholder name is required' : '',
            complete: value.length > 0,
            value: value,
        }
        setState({...state, cardholder})
    }

    const onElementChange = (e) => {
        const err = e.error ? e.error.message : '';
        setState({...state, [e.elementType]: {error: err, complete: e.complete}})
    }

    const onSubmit = (e) => {
        //console.log('onSubmit e=', e)
        const cardElement = elements.getElement(CardNumberElement);
        setState({...state, isFetching: true})
        stripe.createToken(cardElement, {name: state.cardholder.value})
            .then((result) => {

                // Send token to the server to turn into a source...
                AccountService.setPaymentMethod(accountId, result.token.id)
                    .then((result) => {
                        setState({...state, done: true, isFetching: false})

                        // Update the account
                        dispatch(setAccountPaymentMethod(result))

                        // Inform the user it worked
                        toast.success('Your card was added', enqueueSnackbar, closeSnackbar)

                    })
                    .catch((error) => {
                        errorMessage(error);
                        setState({...state, isFetching: false})
                    })

            })
            .catch((error) => {
                errorMessage(error);
                setState({...state, isFetching: false})
            })
    }

    const errorMessage = (error) => {
        if (error.status === 402) {
            // It's a card error, trim the prefix and use the remainder as the error message
            const msg = error.data.error.replace("card add error: ", "")
            toast.apiError(msg, error, enqueueSnackbar);
        } else {
            // It's a generic error?
            toast.apiError("Your card could not be added", error, enqueueSnackbar);
        }
    }

    return (
        <div className="w-full">
            <Typography variant="h6">Add Payment Method</Typography>

            <TextField
                label="Cardholder Name"
                name="cardholderName"
                variant="outlined"
                placeholder="Name"
                InputLabelProps={{shrink: true}}
                required
                fullWidth
                margin="normal"
                value={state.cardholder.value}
                onChange={onCardholderChange}
                onBlur={validateCardholder}
                helperText={<span style={{color: 'red'}}>{state.cardholder.error}</span>}
            />

            <TextField
                label="Card Number"
                name="cardNumber"
                variant="outlined"
                required
                fullWidth
                InputLabelProps={{shrink: true}}
                InputProps={{
                    inputComponent: StripeInput,
                    inputProps: {
                        component: CardNumberElement,
                        options: elOptions,
                        onChange: onElementChange,
                    },
                }}
                helperText={<span style={{color: 'red'}}>{state.cardNumber.error}</span>}
                margin="normal"
            />

            <div className="flex flex-row space-x-3">
                <TextField
                    label="Expiry Date"
                    name="cardExpiry"
                    variant="outlined"
                    required
                    fullWidth
                    InputLabelProps={{shrink: true}}
                    InputProps={{
                        inputComponent: StripeInput,
                        inputProps: {
                            component: CardExpiryElement,
                            options: elOptions,
                            onChange: onElementChange,
                        },
                    }}
                    helperText={<span style={{color: 'red'}}>{state.cardExpiry.error}</span>}
                    margin="normal"
                />

                <TextField
                    label="CVC"
                    name="cardCvc"
                    variant="outlined"
                    required
                    fullWidth
                    InputLabelProps={{shrink: true}}
                    InputProps={{
                        inputComponent: StripeInput,
                        inputProps: {
                            component: CardCvcElement,
                            options: elOptions,
                            onChange: onElementChange,
                        },
                    }}
                    helperText={<span style={{color: 'red'}}>{state.cardCvc.error}</span>}
                    margin="normal"
                />
            </div>

            <div className="flex flex-row">
                <Info fontSize="small" color="primary"/>
                <div className="ml-2 text-xs">
                    Your card information is securely stored by our payments
                    partner <a href="https://stripe.com" target="_blank" rel="noopener noreferrer">
                    <strong>Stripe</strong>
                </a>, one of the world's leading online
                    payment providers. We do not store your card details on our servers.
                </div>
            </div>

            <Button
                type="submit"
                variant="contained"
                color="secondary"
                disabled={!canSubmit() || state.isFetching}
                onClick={onSubmit}
                style={{width: '100%', marginTop: 12}}
            >
                {state.isFetching ?
                    <CircularProgress size={24} />
                    : 'Add Card'}
            </Button>
        </div>
    )
}

export default AddPaymentMethod;